import { useState, useRef, useEffect, useMemo } from 'react';
import { X, Calendar, User, Tag, Zap, Trash2, CreditCard as Edit3, Search, Save } from 'lucide-react';
import type { Task, Priority, SubTask } from '../../types';
import { useApp } from '../../context/AppContext';
import { getStatusConfig, getPriorityConfig, formatDate } from '../../utils/helpers';
import Avatar from '../common/Avatar';
import Badge from '../common/Badge';
import { LabelBadge } from '../common/Badge';
import ConfirmDialog from '../common/ConfirmDialog';
import RichTextEditor from '../common/RichTextEditor';
import DatePickerDropdown from '../common/DatePickerDropdown';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import SubtaskList from './SubtaskList';

interface TaskModalProps {
  task: Task;
  onClose: () => void;
}

const priorities: Priority[] = ['low', 'medium', 'high', 'critical'];

export default function TaskModal({ task, onClose }: TaskModalProps) {
  const {
    getUserById,
    getLabelById,
    state,
    apiUpdateTask,
    apiDeleteTask,
    apiAddSubtask,
    apiUpdateSubtask,
    apiDeleteSubtask,
    getBoardById,
  } = useApp();

  const board = getBoardById(task.boardId);
  const boardStatuses = board?.columns.map((col) => col.status) || [];

  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [activeTab, setActiveTab] = useState<'comments' | 'attachments' | 'subtasks'>('comments');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const assigneeDropdownRef = useRef<HTMLDivElement>(null);

  const [draftTask, setDraftTask] = useState<Task>(task);

  useEffect(() => {
    setDraftTask(task);
    setEditingTitle(false);
    setEditingDesc(false);
    setAssigneeSearch('');
    setShowAssigneeDropdown(false);
  }, [task.id]);

  const reporter = getUserById(draftTask.reporterId);
  const assignees = draftTask.assigneeIds.map((id) => getUserById(id)).filter(Boolean);
  const taskLabels = draftTask.labelIds.map((id) => getLabelById(id)).filter(Boolean);
  const status = getStatusConfig(draftTask.status);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(e.target as Node)
      ) {
        setShowAssigneeDropdown(false);
        setAssigneeSearch('');
      }
    }

    if (showAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAssigneeDropdown]);

  const filteredUsers = state.users.filter(
    (u) =>
      !draftTask.assigneeIds.includes(u.id) &&
      u.name.toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const isDirty = useMemo(() => {
    return JSON.stringify({
      title: draftTask.title,
      description: draftTask.description,
      status: draftTask.status,
      priority: draftTask.priority,
      assigneeIds: draftTask.assigneeIds,
      labelIds: draftTask.labelIds,
      dueDate: draftTask.dueDate,
      subtasks: draftTask.subtasks,
    }) !== JSON.stringify({
      title: task.title,
      description: task.description,
      status: task.status,
      priority: task.priority,
      assigneeIds: task.assigneeIds,
      labelIds: task.labelIds,
      dueDate: task.dueDate,
      subtasks: task.subtasks,
    });
  }, [draftTask, task]);

  const updateDraftTask = (partial: Partial<Task>) => {
    setDraftTask((prev) => ({ ...prev, ...partial }));
  };

  const saveTitle = () => {
    const nextTitle = draftTask.title.trim();
    if (!nextTitle) {
      setDraftTask((prev) => ({ ...prev, title: task.title }));
    } else {
      setDraftTask((prev) => ({ ...prev, title: nextTitle }));
    }
    setEditingTitle(false);
  };

  const saveDesc = () => {
    setEditingDesc(false);
  };

  async function handleSaveAll() {
    if (isSaving || !isDirty) return;

    setIsSaving(true);

    try {
      const taskPayload: Partial<Task> = {};

      if (draftTask.title !== task.title) taskPayload.title = draftTask.title;
      if (draftTask.description !== task.description) taskPayload.description = draftTask.description;
      if (draftTask.status !== task.status) taskPayload.status = draftTask.status;
      if (draftTask.priority !== task.priority) taskPayload.priority = draftTask.priority;
      if (JSON.stringify(draftTask.assigneeIds) !== JSON.stringify(task.assigneeIds)) {
        taskPayload.assigneeIds = draftTask.assigneeIds;
      }
      if (JSON.stringify(draftTask.labelIds) !== JSON.stringify(task.labelIds)) {
        taskPayload.labelIds = draftTask.labelIds;
      }
      if (draftTask.dueDate !== task.dueDate) taskPayload.dueDate = draftTask.dueDate;

      if (Object.keys(taskPayload).length > 0) {
        await apiUpdateTask({ id: task.id, ...taskPayload });
      }

      const originalSubtasks = task.subtasks;
      const nextSubtasks = draftTask.subtasks;

      const originalMap = new Map(originalSubtasks.map((s) => [s.id, s]));
      const nextMap = new Map(nextSubtasks.map((s) => [s.id, s]));

      const toCreate = nextSubtasks.filter((s) => s.id.startsWith('temp-'));
      const toDelete = originalSubtasks.filter((s) => !nextMap.has(s.id));
      const toUpdate = nextSubtasks.filter((s) => {
        if (s.id.startsWith('temp-')) return false;
        const original = originalMap.get(s.id);
        if (!original) return false;

        return JSON.stringify({
          title: s.title,
          assigneeId: s.assigneeId,
          hours: s.hours,
          startDate: s.startDate,
          endDate: s.endDate,
          completed: s.completed,
        }) !== JSON.stringify({
          title: original.title,
          assigneeId: original.assigneeId,
          hours: original.hours,
          startDate: original.startDate,
          endDate: original.endDate,
          completed: original.completed,
        });
      });

      await Promise.all([
        ...toCreate.map((subtask) =>
          apiAddSubtask(task.id, {
            title: subtask.title,
            assigneeId: subtask.assigneeId ?? null,
            hours: subtask.hours ?? 0,
            startDate: subtask.startDate ?? null,
            endDate: subtask.endDate ?? null,
            completed: !!subtask.completed,
          })
        ),
        ...toUpdate.map((subtask) =>
          apiUpdateSubtask({
            id: subtask.id,
            title: subtask.title,
            assigneeId: subtask.assigneeId ?? null,
            hours: subtask.hours ?? 0,
            startDate: subtask.startDate ?? null,
            endDate: subtask.endDate ?? null,
            completed: !!subtask.completed,
          })
        ),
        ...toDelete.map((subtask) => apiDeleteSubtask(subtask.id)),
      ]);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center pt-4 md:pt-12 px-2 md:px-4 animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={draftTask.title}
    >
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col animate-scale-in overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-xs font-mono text-slate-400 shrink-0">{draftTask.id.toUpperCase()}</span>
            <Badge className={status.color}>{status.label}</Badge>
            {isDirty && (
              <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-medium">
                Alterações pendentes
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveAll}
              disabled={!isDirty || isSaving}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={16} />
              {isSaving ? 'Salvando...' : 'Salvar alterações'}
            </button>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              aria-label="Excluir tarefa"
            >
              <Trash2 size={18} />
            </button>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col lg:flex-row">
            <div className="flex-1 p-4 md:p-6 space-y-6 min-w-0">
              {editingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={draftTask.title}
                    onChange={(e) => updateDraftTask({ title: e.target.value })}
                    onBlur={saveTitle}
                    onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
                    className="flex-1 text-2xl font-semibold text-slate-800 px-3 py-2 border border-brand-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-300"
                    autoFocus
                  />
                </div>
              ) : (
                <div
                  className="group flex items-center gap-2 cursor-pointer"
                  onClick={() => setEditingTitle(true)}
                >
                  <h2 className="text-2xl font-semibold text-slate-800 leading-snug break-words">
                    {draftTask.title}
                  </h2>
                  <Edit3
                    size={14}
                    className="mt-1.5 text-slate-300  group-hover:opacity-100 transition-opacity shrink-0"
                  />
                </div>
              )}

              {editingDesc ? (
                <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/60">
                  <RichTextEditor
                    value={draftTask.description}
                    onChange={(value) => updateDraftTask({ description: value })}
                  />
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={saveDesc}
                      className="px-3 py-1.5 rounded-lg bg-brand-500 text-white text-xs font-medium hover:bg-brand-600 transition-colors"
                    >
                      Concluir edição
                    </button>
                    <button
                      onClick={() => {
                        updateDraftTask({ description: task.description });
                        setEditingDesc(false);
                      }}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="group cursor-pointer rounded-2xl border border-slate-200 bg-slate-50/60 p-4 hover:border-slate-300 transition-colors"
                  onClick={() => setEditingDesc(true)}
                >
                  {draftTask.description ? (
                    <div
                      dangerouslySetInnerHTML={{ __html: draftTask.description }}
                      className="prose prose-sm max-w-none text-slate-600"
                    />
                  ) : (
                    <p className="text-sm text-slate-400 italic">Clique para adicionar descrição...</p>
                  )}
                </div>
              )}

              <div className="border-t border-slate-100 pt-4">
                <div className="flex gap-2 mb-4 flex-wrap">
                  <button
                    onClick={() => setActiveTab('comments')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'comments'
                        ? 'bg-brand-50 text-brand-600'
                        : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    Comentários ({task.comments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('attachments')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'attachments'
                        ? 'bg-brand-50 text-brand-600'
                        : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    Anexos ({task.attachments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('subtasks')}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === 'subtasks'
                        ? 'bg-brand-50 text-brand-600'
                        : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    Subtarefas ({draftTask.subtasks.length})
                  </button>
                </div>

                {activeTab === 'comments' && (
                  <TaskComments taskId={task.id} comments={task.comments} />
                )}

                {activeTab === 'attachments' && (
                  <TaskAttachments taskId={task.id} attachments={task.attachments} />
                )}

                {activeTab === 'subtasks' && (
                  <SubtaskList
                    subtasks={draftTask.subtasks}
                    onChange={(subtasks: SubTask[]) => updateDraftTask({ subtasks })}
                  />
                )}
              </div>
            </div>

            <div className="lg:w-80 border-t lg:border-t-0 lg:border-l border-slate-100 p-4 md:p-6 space-y-5 bg-slate-50/60 shrink-0">
              <SidebarField icon={Zap} label="Status">
                <select
                  value={draftTask.status}
                  onChange={(e) => updateDraftTask({ status: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                  aria-label="Status da tarefa"
                >
                  {boardStatuses.map((s) => (
                    <option key={s} value={s}>
                      {getStatusConfig(s).label}
                    </option>
                  ))}
                </select>
              </SidebarField>

              <SidebarField icon={Zap} label="Prioridade">
                <select
                  value={draftTask.priority}
                  onChange={(e) => updateDraftTask({ priority: e.target.value as Priority })}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                  aria-label="Prioridade da tarefa"
                >
                  {priorities.map((p) => (
                    <option key={p} value={p}>
                      {getPriorityConfig(p).label}
                    </option>
                  ))}
                </select>
              </SidebarField>

              <SidebarField icon={User} label="Responsáveis">
                <div className="space-y-2">
                  {assignees.map((user) => (
                    <div
                      key={user!.id}
                      className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2"
                    >
                      <Avatar name={user!.name} color={user!.avatarColor} size="sm" />
                      <span className="text-sm text-slate-700 flex-1">{user!.name}</span>
                      <button
                        onClick={() =>
                          updateDraftTask({
                            assigneeIds: draftTask.assigneeIds.filter((id) => id !== user!.id),
                          })
                        }
                        className="p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        aria-label={`Remover ${user!.name}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}

                  <div ref={assigneeDropdownRef} className="relative">
                    <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-brand-300 focus-within:border-brand-400 transition-all">
                      <Search size={14} className="text-slate-400 shrink-0" />
                      <input
                        type="text"
                        value={assigneeSearch}
                        onChange={(e) => {
                          setAssigneeSearch(e.target.value);
                          setShowAssigneeDropdown(true);
                        }}
                        onFocus={() => setShowAssigneeDropdown(true)}
                        placeholder="Buscar responsável..."
                        className="w-full bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400"
                        aria-label="Buscar responsável"
                      />
                    </div>

                    {showAssigneeDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                        <div className="max-h-48 overflow-y-auto py-1">
                          {filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                updateDraftTask({ assigneeIds: [...draftTask.assigneeIds, user.id] });
                                setAssigneeSearch('');
                                setShowAssigneeDropdown(false);
                              }}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-slate-50 transition-colors text-slate-700"
                            >
                              <Avatar name={user.name} color={user.avatarColor} size="sm" />
                              <span className="truncate">{user.name}</span>
                            </button>
                          ))}

                          {filteredUsers.length === 0 && (
                            <div className="px-3 py-3 text-xs text-slate-400 text-center">
                              Nenhum usuário encontrado
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </SidebarField>

              <SidebarField icon={User} label="Relator">
                {reporter && (
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                    <Avatar name={reporter.name} color={reporter.avatarColor} size="sm" />
                    <span className="text-sm text-slate-700">{reporter.name}</span>
                  </div>
                )}
              </SidebarField>

              <SidebarField icon={Tag} label="Etiquetas">
                <div className="flex flex-wrap gap-2">
                  {taskLabels.map((label) => (
                    <span key={label!.id} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-1.5 py-1">
                      <LabelBadge name={label!.name} color={label!.color} />
                      <button
                        onClick={() =>
                          updateDraftTask({
                            labelIds: draftTask.labelIds.filter((id) => id !== label!.id),
                          })
                        }
                        className="p-0.5 rounded text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                        aria-label={`Remover ${label!.name}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}

                  <select
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val && !draftTask.labelIds.includes(val)) {
                        updateDraftTask({ labelIds: [...draftTask.labelIds, val] });
                      }
                      e.target.value = '';
                    }}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-300"
                    defaultValue=""
                    aria-label="Adicionar etiqueta"
                  >
                    <option value="" disabled>
                      Adicionar etiqueta...
                    </option>
                    {state.labels
                      .filter((l) => !draftTask.labelIds.includes(l.id))
                      .map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                  </select>
                </div>
              </SidebarField>

              <SidebarField icon={Calendar} label="Prazo">
                <DatePickerDropdown
                  value={draftTask.dueDate ? draftTask.dueDate.split('T')[0] : null}
                  onChange={(val) =>
                    updateDraftTask({
                      dueDate: val ? `${val}T23:59:59Z` : null,
                    })
                  }
                  placeholder="Selecionar prazo"
                  showPresets
                />
              </SidebarField>

              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs text-slate-400">
                  Criado em {formatDate(draftTask.createdAt)}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Atualizado em {formatDate(draftTask.updatedAt)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Excluir tarefa"
        message="Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        onConfirm={async () => {
          await apiDeleteTask(task.id);
          onClose();
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}

function SidebarField({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} className="text-slate-400" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          {label}
        </span>
      </div>
      {children}
    </div>
  );
}